import {Injectable, OnDestroy} from '@angular/core';
import {ApiService} from "./api/api.service";
import {Subject} from "rxjs";
import {Rule, RuleSet} from '../_typing/query-builder';
import {deepCopy} from '../lib/utils';
import {getRelationWithIdFilter} from "./api/filter_utils";
import {AppScope} from "./app_scope.service";
import {ModelID} from "../_typing/generic-types";

@Injectable({
    providedIn: 'root'
})
export class QueryBuilderService implements OnDestroy {
    private readonly onDestroy = new Subject<void>();
    operatorMap = {
        '=': 'eq',
        '!=': 'ne',
        '>': 'gt',
        '>=': 'ge',
        '<': 'lt',
        '<=': 'le',
        'not in': 'notin_',
        'not like': 'notlike',
        'not ilike': 'notilike'
    };

    inverseOperator = {
        '!=': '=',
        'not in': 'in',
        'not like': 'like',
        'not ilike': 'ilike',
    };

    singleRelationships = ["changed_by", "created_by"];

    constructor(private api: ApiService, private appScope: AppScope) {
    }

    toJsonQuery(query: RuleSet, modelType = 'COMPONENT') {
        let rules = deepCopy(query).rules.map((rule: RuleSet | Rule) => {
            if ('field' in rule) {
                return this.toJsonQueryRule(rule, modelType);
            } else if ('condition' in rule) {
                return this.toJsonQuery(rule, modelType);
            }
        });
        rules = rules.filter(rule => rule);
        return {[query.condition]: rules};
    }

    toJsonQueryRule(rule: Rule, modelType: string) {
        let cp_match = rule.field.match(/^\[(\w+)@([a-f0-9-]+)(?:@(\w+)@([a-f0-9-]+))?\]$/);
        rule.value = this.wrapILikeValue(rule);
        rule = this.checkIsBlank(rule);
        rule = this.checkHasAccess(rule);
        if (!cp_match) {
            if (rule.field.indexOf('component@') > -1) {
                return modelType === 'COMPONENT' ? this.relationshipAttributeQuery(rule) : this.eventRelationshipAttributeQuery(rule);
            } else {
                if (Array.isArray(rule.value)) {
                    return {'name': rule.field, 'op': rule.operator, 'val': rule.value};
                }
                return this.attributeQuery(rule);

            }
        } else if (cp_match[1] === 'constant_property') {
            if (!rule.value && rule.value !== 0) {
                return this.constantPropertyQueryNull(rule, cp_match[2], modelType);
            } else {
                return this.constantPropertyQuery(rule, cp_match[2], modelType);
            }
        } else if (modelType === 'COMPONENT') {
            if (cp_match[1] === 'component_type') {
                return this.componentComponentTypeQuery(rule, cp_match[2]);
            } else if (cp_match[1] === 'event_type') {
                return this.componentEventTypeQuery(rule, cp_match[2]);
            } else if (cp_match[1] === 'component' && cp_match[3] === 'name') {
                return this.relatedPropertyQuery(rule, cp_match[2], cp_match[4]);
            } else if (cp_match[1] === 'component' && cp_match[3] === 'constant_property') {
                return this.relatedPropertyQuery(rule, cp_match[2], cp_match[4]);
            }
        } else if (modelType === 'EVENT') {
            if (cp_match[1] === 'component_type') {
                return this.eventComponentTypeQuery(rule, cp_match[2]);
            }
        }
    }

    checkIsBlank(rule) {
        if (rule.operator === 'blank') {
            rule.operator = '=';
            rule.value = null;
        } else if (rule.operator === 'not blank') {
            rule.operator = '!=';
            rule.value = null;
        }
        return rule;
    }

    checkHasAccess(rule) {
        if (rule.operator === 'current user') {
            rule.operator = '=';
            rule.value = this.appScope.current_user.id;
        } else if (rule.operator === 'not current user') {
            rule.operator = '!=';
            rule.value = this.appScope.current_user.id;
        } else if (rule.operator === 'can access group') {
            rule.operator = 'in';
            // Get all group IDs the user belongs to
            rule.value = this.appScope.current_user.groups?.map(g => g.id.toString()) || [];
        } else if (rule.operator === 'can not access group') {
            rule.operator = 'notin_';
            // Get all group IDs the user belongs to
            rule.value = this.appScope.current_user.groups?.map(g => g.id.toString()) || [];
        } else if (rule.operator === 'can access account') {
            rule.operator = 'in';
            // Get all account IDs the user belongs to
            rule.value = this.appScope.current_user.accounts || [];
        } else if (rule.operator === 'can not access account') {
            rule.operator = 'notin_';
            // Get all accounts IDs the user belongs to
            rule.value = this.appScope.current_user.accounts || [];
        } else if (rule.operator === 'current account') {
            rule.operator = '=';
            rule.value = this.appScope.active_account_id;
        } else if (rule.operator === 'not current account') {
            rule.operator = '!=';
            rule.value = this.appScope.active_account_id;
        }

        return rule;
    }

    wrapILikeValue(rule) {
        if (rule.operator.includes('like') && !rule.value?.includes('%')) {
            rule.value = `%${rule.value}%`;
        }
        return rule.value;
    }

    attributeQuery(rule: Rule) {
        let op = this.operatorMap[rule.operator] || rule.operator;
        if (this.singleRelationships.includes(rule.field)) {
            return this.relationshipQuery(rule);
        }
        if (rule.value) {
            // For 'in' and 'notin_' operators, convert comma-separated string to array
            if (op === 'in' || op === 'notin_') {
                return {'name': rule.field, 'op': op, 'val': rule.value.split(',')};
            }
            return {'name': rule.field, 'op': op, 'val': rule.value};
        } else {
            op = rule.operator === '=' ? 'eq' : 'ne';
            return {'name': rule.field, 'op': op, val: null};
        }
    }

    relationshipAttributeQuery(rule) {
        const componentTypeId = rule.field.substring(rule.field.indexOf("@") + 1, rule.field.lastIndexOf("@"));
        const componentTypeQuery = getRelationWithIdFilter('component_type', componentTypeId);
        const attribute = rule.field.substring(rule.field.lastIndexOf("@") + 1, rule.field.length - 1);
        let tempRule = deepCopy(rule);
        tempRule.field = attribute;
        const attributeQuery = this.attributeQuery(tempRule);

        let query: any = {
            or: [{
                and: [
                    {
                        name: 'component_component',
                        op: 'any',
                        val: {name: 'first_component', op: 'has', val: componentTypeQuery}
                    },
                    {
                        name: 'component_component',
                        op: 'any',
                        val: {name: 'first_component', op: 'has', val: attributeQuery}
                    },

                ]
            },
                {
                    and: [
                        {
                            name: 'component_component',
                            op: 'any',
                            val: {name: 'second_component', op: 'has', val: componentTypeQuery}
                        },
                        {
                            name: 'component_component',
                            op: 'any',
                            val: {name: 'second_component', op: 'has', val: attributeQuery}
                        },

                    ]
                }
            ]
        }

        return query;
    }

    eventRelationshipAttributeQuery(rule) {
        const componentTypeId = rule.field.substring(rule.field.indexOf("@") + 1, rule.field.lastIndexOf("@"));
        const componentTypeQuery = getRelationWithIdFilter('component_type', componentTypeId);
        const attribute = rule.field.substring(rule.field.lastIndexOf("@") + 1, rule.field.length - 1);
        let tempRule = deepCopy(rule);
        tempRule.field = attribute;
        const attributeQuery = this.attributeQuery(tempRule);

        let query: any = {
            or: [{
                and: [
                    {
                        name: 'event_components',
                        op: 'any',
                        val: {name: 'component', op: 'has', val: componentTypeQuery}
                    },
                    {
                        name: 'event_components',
                        op: 'any',
                        val: {name: 'component', op: 'has', val: attributeQuery}
                    },

                ]
            }]
        }

        return query;
    }

    relationshipQuery(rule) {
        let op = this.operatorMap[rule.operator] || rule.operator;
        if (rule.value) {
            return {name: rule.field, op: 'has', val: {name: 'id', op: op, val: rule.value}}
        } else {
            op = rule.operator === '=' ? 'eq' : 'ne';
            return {'name': rule.field, 'op': op, val: null};
        }
    }

    relatedPropertyQuery(rule, componentTypeId, constantPropertyId) {
        const startIndex = rule.field.indexOf("constant_property@");
        const propertyRule = Object.assign(rule, {field: "[" + rule.field.slice(startIndex, -1) + "]"});
        const componentTypeQuery = getRelationWithIdFilter('component_type', componentTypeId);

        const propertyQuery = (propertyRule.value || propertyRule.value === 0) ? this.constantPropertyQuery(propertyRule, constantPropertyId, 'COMPONENT') :
            this.constantPropertyQueryNull(propertyRule, constantPropertyId, 'COMPONENT');

        let query: any = {
            or: [{
                and: [
                    {
                        name: 'component_component',
                        op: 'any',
                        val: {name: 'first_component', op: 'has', val: componentTypeQuery}
                    },
                    {
                        name: 'component_component',
                        op: 'any',
                        val: {name: 'first_component', op: 'has', val: propertyQuery}
                    },

                ]
            },
                {
                    and: [
                        {
                            name: 'component_component',
                            op: 'any',
                            val: {name: 'second_component', op: 'has', val: componentTypeQuery}
                        },
                        {
                            name: 'component_component',
                            op: 'any',
                            val: {name: 'second_component', op: 'has', val: propertyQuery}
                        },

                    ]
                }
            ]
        }

        return query;
    }

    constantPropertyQuery(rule: Rule, constant_property_id: string, modelType: string) {
        let isInverse = false;
        let op = this.operatorMap[rule.operator] || rule.operator;
        if (rule.operator in this.inverseOperator) {
            op = this.operatorMap[this.inverseOperator[rule.operator]] || this.inverseOperator[rule.operator];
            isInverse = true;
        }

        let value_column = '';
        if (typeof (rule.value) === "number") {
            value_column = 'value_float';
        } else if (!(Array.isArray(rule.value)) && rule.value.match(/\d{4}-\d{1,2}-\d{1,2}/) && Date.parse(rule.value)) {
            value_column = 'value_date';
        } else {
            value_column = 'value';
        }
        let tableName = modelType === 'COMPONENT' ? 'constant_components' : 'event_constants';
        let query: any = {
            'name': tableName,
            'op': 'any',
            'val': {
                'and': [
                    {
                        'and': [
                            {
                                'name': 'value',
                                'op': 'isnot',
                                'val': null
                            },
                            {
                                'name': 'value',
                                'op': 'ne',
                                'val': ''
                            },
                            {
                                'name': 'value',
                                'op': 'notin_',
                                'val': ['None', 'nan']
                            }
                        ]
                    },
                    {
                        'name': value_column,
                        'op': op,
                        'val': rule.value ?? null
                    },
                    {
                        'name': 'constant_property',
                        'op': 'has',
                        'val': {'name': 'id', 'op': 'eq', 'val': constant_property_id}
                    }
                ]
            }
        };

        if (isInverse) {
            query = {'not': query};
        }

        const relation = modelType === 'COMPONENT' ? 'component_type' : 'event_type';
        const hasCPRelationship = this.checkHasPropertyQuery(constant_property_id, relation)
        return {or: [{not: hasCPRelationship}, {and: [hasCPRelationship, query]}]};
    }

    constantPropertyQueryNull(rule: Rule, constant_property_id: string, modelType: string) {
        let isNull = rule.operator === '=' ? true : false;
        let tableName = modelType === 'COMPONENT' ? 'constant_components' : 'event_constants';
        let query: any = {
            'name': tableName,
            'op': 'any',
            'val': {
                'and': [
                    {
                        'and': [
                            {
                                'name': 'value',
                                'op': 'isnot',
                                'val': null
                            },
                            {
                                'name': 'value',
                                'op': 'ne',
                                'val': ''
                            },
                            {
                                'name': 'value',
                                'op': 'notin_',
                                'val': ['None', 'nan']
                            }
                        ]
                    },
                    {
                        'name': 'constant_property',
                        'op': 'has',
                        'val': {'name': 'id', 'op': 'eq', 'val': constant_property_id}
                    }
                ]
            }
        };

        if (isNull) {
            query = {'not': query};
        }

        const relation = modelType === 'COMPONENT' ? 'component_type' : 'event_type';
        const hasCPRelationship = this.checkHasPropertyQuery(constant_property_id, relation)
        return {or: [{not: hasCPRelationship}, {and: [hasCPRelationship, query]}]};
    }

    checkHasPropertyQuery(constantPropertyId: ModelID, relation: 'component_type' | 'event_type') {
        return {
            "name": relation,
            "op": "has",
            "val": {
                "name": "constant_properties",
                "op": "any",
                "val": {
                    "name": "id",
                    "op": "eq",
                    "val": constantPropertyId
                }
            }
        }
    }

    componentComponentTypeQuery(rule: Rule, component_type: string) {
        let query: any = {
            name: 'component_component',
            op: 'any',
            val: {
                or: [
                    {
                        name: 'first_component',
                        op: 'has',
                        val: {name: 'component_type', op: 'has', val: {name: 'id', op: 'eq', val: component_type}}
                    },
                    {
                        name: 'second_component',
                        op: 'has',
                        val: {name: 'component_type', op: 'has', val: {name: 'id', op: 'eq', val: component_type}}
                    }
                ]
            }
        };

        if (!rule.value) {
            query = {'not': query};
        }
        return query;
    }

    componentEventTypeQuery(rule: Rule, event_type: string) {
        let query: any = {
            name: 'event_components',
            op: 'any',
            val: {
                name: 'event',
                op: 'has',
                val: {name: 'event_type', op: 'has', val: {name: 'id', op: 'eq', val: event_type}}
            }
        };

        if (!rule.value) {
            query = {'not': query};
        }
        return query;
    }

    eventComponentTypeQuery(rule: Rule, component_type: string) {
        let query: any = {
            name: 'event_components',
            op: 'any',
            val: {
                name: 'component',
                op: 'has',
                val: {name: 'component_type', op: 'has', val: {name: 'id', op: 'eq', val: component_type}}
            }
        };

        if (!rule.value) {
            query = {'not': query};
        }
        return query;
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.unsubscribe();
    }
}
